import { useRef, useEffect } from 'preact/hooks';
import { Message } from './Message';
import { TypingIndicator } from './TypingIndicator';
import { SuggestedQuestions } from './SuggestedQuestions';
import type { Message as MessageType } from '@/types';
import s from './MessagesArea.module.css';

interface MessagesAreaProps {
  messages: MessageType[];
  isLoading: boolean;
  onRate: (messageId: string, rating: 'positive' | 'negative', feedback?: string) => void;
  onEmailSubmit: (questionId: string, email: string) => Promise<void>;
  /** Questions shown on empty chat (before user sends first message) */
  suggestedQuestions?: string[];
  /** Questions shown after last bot response */
  followUpQuestions?: string[] | null;
  /** Whether user has sent at least one message */
  hasSentMessage?: boolean;
  /** Handler for when user clicks a suggested/follow-up question */
  onQuestionClick?: (question: string) => void;
}

export function MessagesArea({
  messages,
  isLoading,
  onRate,
  onEmailSubmit,
  suggestedQuestions,
  followUpQuestions,
  hasSentMessage,
  onQuestionClick,
}: MessagesAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastBotMessageRef = useRef<HTMLDivElement>(null);

  // Scroll behavior depends on what happened:
  // - User sent message → scroll to bottom
  // - Bot responded → scroll to start of bot message
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    if (lastMessage.role === 'user' || isLoading) {
      // User just sent a message or waiting for bot response → scroll to bottom
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else if (lastMessage.role === 'bot') {
      // Bot responded → scroll to start of bot message
      // Use manual offset calculation to scroll within the container (not the window)
      const lastBotMsg = lastBotMessageRef.current;
      if (lastBotMsg) {
        const targetTop = lastBotMsg.offsetTop - el.offsetTop;
        el.scrollTo({ top: targetTop, behavior: 'smooth' });
      }
    }
  }, [messages.length, isLoading]);

  // Find index of last bot message
  const lastBotMessageIndex = messages.findLastIndex((m) => m.role === 'bot');

  // Show starter suggested questions only if user hasn't sent a message yet
  const showStarterQuestions =
    !hasSentMessage &&
    suggestedQuestions &&
    suggestedQuestions.length > 0 &&
    onQuestionClick;

  // Show follow-up questions if they exist and we're not loading
  const showFollowUpQuestions =
    !isLoading &&
    followUpQuestions &&
    followUpQuestions.length > 0 &&
    onQuestionClick;

  return (
    <div
      ref={scrollRef}
      class={s.area}
      role="log"
      aria-label="Chat messages"
      aria-live="polite"
    >
      {messages.map((msg, index) => (
        <div
          key={msg.id}
          ref={index === lastBotMessageIndex && msg.role === 'bot' ? lastBotMessageRef : undefined}
        >
          <Message
            message={msg}
            onRate={onRate}
            onEmailSubmit={onEmailSubmit}
          />
        </div>
      ))}
      {isLoading && <TypingIndicator />}

      {/* Show starter suggested questions after welcome message */}
      {showStarterQuestions && (
        <SuggestedQuestions
          questions={suggestedQuestions}
          onQuestionClick={onQuestionClick}
        />
      )}

      {/* Show follow-up questions after last bot response */}
      {showFollowUpQuestions && (
        <SuggestedQuestions
          questions={followUpQuestions}
          onQuestionClick={onQuestionClick}
        />
      )}
    </div>
  );
}
